import type { NlpFilters } from '@massilia/queries'

import type {
  LuggageFilterValue,
  StopsFilterValue,
} from '@/lib/schemas/flight-results-search.schema'

import type { SegmentTimeFilter } from './time-filter'

export interface FlightUrlFilterParams {
  filterAirlines?: string
  filterDurationMax?: number
  filterDurationMin?: number
  filterLuggage?: LuggageFilterValue
  filterPriceMax?: number
  filterPriceMin?: number
  filterStops?: StopsFilterValue
  filterTimeRanges?: string
}

// Patch passed to setFilters() to clear all sidebar filters at once. Centralized
// here so the sidebar "Réinitialiser" button and the empty-state card stay in
// sync — adding a new filter field requires touching only this object.
export const RESET_FILTERS_PATCH: Partial<FlightUrlFilterParams> = {
  filterAirlines: undefined,
  filterDurationMax: undefined,
  filterDurationMin: undefined,
  filterLuggage: undefined,
  filterPriceMax: undefined,
  filterPriceMin: undefined,
  filterStops: undefined,
  filterTimeRanges: undefined,
}

/**
 * Construit un intervalle de filtre à partir des deux bornes de l'URL.
 *
 * Une borne SEULE est une contrainte valide : le chat n'exprime que des
 * plafonds (« moins de 3000€ »), jamais de plancher. Exiger les deux rendait
 * tout plafond venu du chat silencieusement inopérant — le bot annonçait
 * « OK : moins de 3000€ ✅ » et la liste ne bougeait pas. La borne absente
 * s'ouvre (0 / +∞) au lieu d'annuler le filtre.
 *
 * Partagé par /vols/resultats et /vols-multi/resultats : la règle était copiée
 * dans les deux, et c'est cette duplication qui a laissé le bug vivre des deux
 * côtés.
 */
export function boundsToRange(
  min: number | undefined,
  max: number | undefined
): undefined | { max: number; min: number } {
  if (min === undefined && max === undefined) return undefined
  return { max: max ?? Number.POSITIVE_INFINITY, min: min ?? 0 }
}

/**
 * Fusion des filtres émis par le chat dans les search params de la route.
 *
 * `navigate({ search: (prev) => … })` remplace l'objet search ENTIER, et celui-ci
 * ne contient pas que des filtres : il porte `search` (la
 * `StructuredMultiSearchRequest`, c'est-à-dire la recherche elle-même) et
 * `filterSort`. Partir de `{}` en mode `replace` supprimait donc `search`, la
 * requête se désactivait, et TOUT retrait de filtre réussi vidait la page de ses
 * résultats. Le mode `replace` est une remise à zéro des FILTRES, pas de l'URL :
 * il part de `prev` et applique `RESET_FILTERS_PATCH` — le même objet que le
 * bouton « Réinitialiser » de la sidebar, donc plus de divergence possible quand
 * un filtre s'ajoute.
 */
export function buildFilterSearchPatch(
  prev: Record<string, unknown>,
  nextParams: FlightUrlFilterParams,
  replace: boolean
): Record<string, unknown> {
  const base = replace ? { ...prev, ...RESET_FILTERS_PATCH } : prev
  const merged: Record<string, unknown> = { ...base, ...nextParams }
  // Une borne posée seule efface sa jumelle : sinon « moins de 500€ » cohabite
  // avec un ancien plancher à 800€ et la liste sort vide.
  if (nextParams.filterPriceMax !== undefined && nextParams.filterPriceMin === undefined) {
    merged.filterPriceMin = undefined
  }
  if (nextParams.filterPriceMin !== undefined && nextParams.filterPriceMax === undefined) {
    merged.filterPriceMax = undefined
  }
  if (nextParams.filterDurationMax !== undefined && nextParams.filterDurationMin === undefined) {
    merged.filterDurationMin = undefined
  }
  if (nextParams.filterDurationMin !== undefined && nextParams.filterDurationMax === undefined) {
    merged.filterDurationMax = undefined
  }
  return merged
}

export function nlpFiltersToUrlParams(filters: NlpFilters): FlightUrlFilterParams {
  const out: FlightUrlFilterParams = {}
  // `'all'` DOIT partir dans l'URL : c'est la valeur neutre par laquelle le chat
  // retire un filtre d'escales. La jeter ne produisait aucun paramètre, donc en
  // fusion l'ancien `filterStops=direct` survivait — pendant que le bot
  // confirmait « OK : toutes escales ✅ ». Sans risque : `filterStops` a déjà
  // `.default('all')` dans les deux routes, `applyFilters` a un `case 'all'`
  // inerte, et `urlParamsToNlpFilters` filtre `'all'` en sortie (donc aucun
  // filtre fantôme ne repart vers le contexte du chat).
  if (filters.stops) out.filterStops = filters.stops
  if (filters.luggage) out.filterLuggage = filters.luggage
  if (typeof filters.maxPrice === 'number' && Number.isFinite(filters.maxPrice)) {
    out.filterPriceMax = filters.maxPrice
  }
  if (
    typeof filters.maxDurationMinutes === 'number' &&
    Number.isFinite(filters.maxDurationMinutes)
  ) {
    out.filterDurationMax = filters.maxDurationMinutes
  }
  if (filters.excludedAirlines && filters.excludedAirlines.length > 0) {
    out.filterAirlines = filters.excludedAirlines.join(',')
  }
  if (filters.timeFilters && filters.timeFilters.length > 0) {
    out.filterTimeRanges = JSON.stringify(filters.timeFilters)
  }
  return out
}

// Normalize the JSON time-filter param coming from the URL. The chat layer may
// emit entries with only departureTimeSlots OR only arrivalTimeSlots (cf.
// NlpSegmentTimeFilter where both are optional); downstream consumers expect
// both arrays to be present, so default the missing one to []. Also tolerates
// malformed JSON and entries without a numeric segmentIndex.
export function parseFilterTimeRanges(raw: string | undefined): SegmentTimeFilter[] {
  if (!raw) return []
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) return []
  return parsed.flatMap((entry): SegmentTimeFilter[] => {
    if (!entry || typeof entry !== 'object') return []
    const e = entry as Partial<SegmentTimeFilter>
    if (typeof e.segmentIndex !== 'number') return []
    return [
      {
        arrivalTimeSlots: Array.isArray(e.arrivalTimeSlots) ? e.arrivalTimeSlots : [],
        departureTimeSlots: Array.isArray(e.departureTimeSlots) ? e.departureTimeSlots : [],
        segmentIndex: e.segmentIndex,
      },
    ]
  })
}

export function urlParamsToNlpFilters(params: FlightUrlFilterParams): NlpFilters {
  const out: NlpFilters = {}
  if (params.filterStops && params.filterStops !== 'all') out.stops = params.filterStops
  // `'0'` n'est pas un filtre : c'est le défaut de `validateSearch`, et
  // `filter-utils.ts` le lit comme « au moins 0 bagage » — il matche tout. Le
  // laisser passer publiait un filtre fantôme dans `pageContext.currentFilters`
  // à CHAQUE tour, que le modèle recopiait consciencieusement en mode `replace`.
  // Filtré ici et pas dans `validateSearch` : le défaut est lu par la sidebar et
  // par l'URL de partage ; cette fonction est la traduction vers le contexte chat.
  if (params.filterLuggage && params.filterLuggage !== '0') out.luggage = params.filterLuggage
  if (typeof params.filterPriceMax === 'number') out.maxPrice = params.filterPriceMax
  if (typeof params.filterDurationMax === 'number') {
    out.maxDurationMinutes = params.filterDurationMax
  }
  if (params.filterAirlines) {
    const codes = params.filterAirlines.split(',').filter(Boolean)
    if (codes.length > 0) out.excludedAirlines = codes
  }
  const timeFilters = parseFilterTimeRanges(params.filterTimeRanges)
  if (timeFilters.length > 0) out.timeFilters = timeFilters
  return out
}
